import { memo, useMemo, useState } from 'react';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef/index.ts';

import { Tooltip } from '../../Feedback/Tooltip/index.tsx';
import { Dropdown, DropdownWithFilterState } from './components/Dropdown/index.tsx';
import { FilterButton } from './components/FilterButton/index.tsx';
import { AUTOFOCUS_WITHOUT_SCROLL } from './components/Search.tsx';
import { SelectedLabel } from './components/SelectedLabel/index.tsx';
import { DEFAULT_DROPDOWN_WIDTH } from './constants.ts';
import type { FilterProps } from './types.ts';

const Component = <T,>(props: FilterProps<T>) => {
  const {
    dropdownAlign,
    label,
    isEnabledSelectedLabel = true,
    dataTestId,
    serverSideFiltering,
    options,
    formatOptionLabel,
    dropdownWidth = DEFAULT_DROPDOWN_WIDTH,
    ...restProps
  } = props;
  const { isMulti, selected, disabled, recover } = restProps;

  const [isShown, setIsShown] = useState<boolean>(false);

  const selectedOptions = useMemo(() => {
    if (isMulti) {
      return selected;
    }

    return selected ? [selected] : [];
  }, [isMulti, selected]);

  const handleClose = useCallbackRef(() => {
    setIsShown(false);
  });

  const serverSideFilteringProps = useMemo(() => {
    if (!serverSideFiltering) return null;

    return {
      serverSideFiltering,
      filter: serverSideFiltering.filter,
      setFilter: serverSideFiltering.setFilter,
      total: serverSideFiltering.total,
    };
  }, [serverSideFiltering]);

  const commonDropdownProps = useMemo(
    () => ({
      ...restProps,
      close: handleClose,
      formatOptionLabel,
      dataTestId: 'dropdown-body',
      dropdownWidth,
    }),
    [restProps, handleClose, formatOptionLabel, dropdownWidth],
  );

  const dropdownComponent = useMemo(() => {
    if (serverSideFilteringProps) {
      return <Dropdown<T> {...commonDropdownProps} {...serverSideFilteringProps} filteredOptions={options} options={options} />;
    }

    return <DropdownWithFilterState<T> {...commonDropdownProps} options={options} />;
  }, [serverSideFilteringProps, commonDropdownProps, options]);

  const buttonContent = useMemo(() => {
    if (selectedOptions.length && isEnabledSelectedLabel) {
      return <SelectedLabel selected={selectedOptions} label={label} formatOptionLabel={formatOptionLabel} />;
    }
    return label;
  }, [selectedOptions, isEnabledSelectedLabel, label, formatOptionLabel]);

  return (
    <FilterButton
      isShown={isShown}
      setIsShown={setIsShown}
      disabled={disabled}
      align={dropdownAlign}
      recover={recover}
      dropdownWidth={dropdownWidth}
      dropdown={dropdownComponent}
      dataTestId={dataTestId}>
      {buttonContent}
    </FilterButton>
  );
};

const Filter = memo(function Filter<T>(props: FilterProps<T> & { tooltip?: string }) {
  const { tooltip, autofocus = AUTOFOCUS_WITHOUT_SCROLL, ...componentProps } = props;

  const componentWithAutofocus = useMemo(() => <Component {...componentProps} autofocus={autofocus} />, [componentProps, autofocus]);

  if (tooltip) {
    return (
      <Tooltip tooltip={tooltip} placement='top'>
        <Component {...componentProps} autofocus={autofocus} />
      </Tooltip>
    );
  }

  return componentWithAutofocus;
}) as <T>(props: FilterProps<T> & { tooltip?: string }) => JSX.Element;

export default Filter;
